import { useId, type FormEvent } from "react";
import {
  RECURRENCES,
  type ComplianceItem,
  type ComplianceInput,
  type Recurrence,
} from "@/api/compliance";
import { RequestError } from "@/components/RequestError";
import { SelectField, TextAreaField, TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { useFormValues, useSubmitState } from "@/lib/use-form";

type ComplianceFormProps = {
  initial: ComplianceItem | null;
  submitLabel: string;
  onSubmit: (input: ComplianceInput) => Promise<unknown>;
  onCancel: () => void;
  onReload?: () => void;
};

export function ComplianceForm({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
  onReload,
}: ComplianceFormProps) {
  const id = useId();
  const { values, set } = useFormValues({
    name: initial?.name ?? "",
    form: initial?.form ?? "",
    provision: initial?.provision ?? "",
    period_label: initial?.period_label ?? "",
    due_on: initial?.due_on ?? "",
    responsible: initial?.responsible ?? "",
    notes: initial?.notes ?? "",
    daily_fee_inr: initial?.daily_fee_inr ? String(Number(initial.daily_fee_inr)) : "",
    recurrence: (initial?.recurrence ?? "none") as Recurrence,
  });
  const { error, errors, saving, run } = useSubmitState();

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() =>
      onSubmit({
        name: values.name.trim(),
        form: values.form.trim(),
        provision: values.provision.trim(),
        period_label: values.period_label.trim(),
        due_on: values.due_on,
        responsible: values.responsible.trim(),
        notes: values.notes.trim(),
        daily_fee_inr: values.daily_fee_inr.trim() || null,
        recurrence: values.recurrence,
      }),
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <RequestError error={error} onReload={onReload} />
      <div className="grid gap-4 md:grid-cols-3">
        <TextField
          id={`${id}-name`}
          label="What"
          value={values.name}
          onChange={(value) => set("name", value)}
          error={errors.name}
          className="md:col-span-2"
          hint="e.g. Financial statements, Annual return, Income tax return"
        />
        <TextField
          id={`${id}-form`}
          label="Form"
          value={values.form}
          onChange={(value) => set("form", value)}
          error={errors.form}
          hint="AOC-4, MGT-7A, ITR-6, AGM..."
        />
        <TextField
          id={`${id}-provision`}
          label="Provision"
          value={values.provision}
          onChange={(value) => set("provision", value)}
          error={errors.provision}
          hint="s.137, Rule 12A - optional"
        />
        <TextField
          id={`${id}-period`}
          label="Period"
          value={values.period_label}
          onChange={(value) => set("period_label", value)}
          error={errors.period_label}
          hint="FY2025-26 or AY2026-27"
        />
        <TextField
          id={`${id}-due`}
          label="Due on"
          type="date"
          value={values.due_on}
          onChange={(value) => set("due_on", value)}
          error={errors.due_on}
        />
        <TextField
          id={`${id}-fee`}
          label="Fee per day late (INR)"
          inputMode="decimal"
          value={values.daily_fee_inr}
          onChange={(value) => set("daily_fee_inr", value)}
          error={errors.daily_fee_inr}
          hint="₹100 on AOC-4 and MGT-7 (s.403); leave empty if none"
        />
        <SelectField
          id={`${id}-recurrence`}
          label="Repeats"
          value={values.recurrence}
          onChange={(value) => set("recurrence", value as Recurrence)}
          options={RECURRENCES}
          error={errors.recurrence}
        />
        <TextField
          id={`${id}-responsible`}
          label="Responsible"
          value={values.responsible}
          onChange={(value) => set("responsible", value)}
          error={errors.responsible}
          hint="CA, CS or a director - optional"
        />
      </div>
      <TextAreaField
        id={`${id}-notes`}
        label="Notes"
        value={values.notes}
        onChange={(value) => set("notes", value)}
        error={errors.notes}
        rows={2}
      />
      <FormActions saving={saving} submitLabel={submitLabel} onCancel={onCancel} />
    </form>
  );
}
